import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { exportToExcel, exportToPDF } from '../../utils/exportUtils';
import { translateLocation } from '../../utils/translationUtils';
import Modal from '../../components/Modal';
import './Payments.css';

const AdminPayments = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { selectedLocation } = useOutletContext() || {};
  const { data, addPayment, registerPayment, updatePayment } = useData();
  const paymentMethodOptions = ['bank transfer', 'cash', 'pin'];

  const [selectedStudentFilter, setSelectedStudentFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('');
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [newPayment, setNewPayment] = useState({
    studentId: '',
    courseId: '',
    totalAmount: '',
    description: '',
    paymentType: 'school-year',
    schoolYear: '',
    dueDate: '',
    paymentMethod: 'bank transfer',
    notes: ''
  });

  const normalizedLocationFilter = useMemo(() => {
    if (!selectedLocation || selectedLocation === 'all') return '';
    return selectedLocation.toLowerCase();
  }, [selectedLocation]);

  const students = useMemo(() => {
    let list = data.students || [];
    if (user.role === 'manager') {
      list = list.filter((s) => (s.location || '').toLowerCase() === (user.location || '').toLowerCase());
    }
    if (normalizedLocationFilter) {
      list = list.filter((s) => (s.location || '').toLowerCase() === normalizedLocationFilter);
    }
    return list;
  }, [data.students, normalizedLocationFilter, user.location, user.role]);

  const courses = useMemo(() => {
    let list = data.courses || [];
    if (user.role === 'manager') {
      list = list.filter((c) => (c.location || '').toLowerCase() === (user.location || '').toLowerCase());
    }
    if (normalizedLocationFilter) {
      list = list.filter((c) => (c.location || '').toLowerCase() === normalizedLocationFilter);
    }
    return list;
  }, [data.courses, normalizedLocationFilter, user.location, user.role]);

  const filteredPayments = useMemo(() => {
    let list = data.payments || [];

    if (user.role === 'manager') {
      list = list.filter((p) => {
        const studentLocation = (p.student?.location || '').toLowerCase();
        const courseLocation = (p.course?.location || '').toLowerCase();
        const managerLocation = (user.location || '').toLowerCase();
        return studentLocation === managerLocation || courseLocation === managerLocation;
      });
    }

    if (normalizedLocationFilter) {
      list = list.filter((p) => {
        const studentLocation = (p.student?.location || '').toLowerCase();
        const courseLocation = (p.course?.location || '').toLowerCase();
        return studentLocation === normalizedLocationFilter || courseLocation === normalizedLocationFilter;
      });
    }

    if (selectedStudentFilter) {
      list = list.filter((p) => String(p.studentId || p.student?.id) === String(selectedStudentFilter));
    }

    if (selectedCourseFilter) {
      list = list.filter((p) => String(p.courseId || p.course?.id) === String(selectedCourseFilter));
    }

    if (selectedStatusFilter) {
      list = list.filter((p) => p.status === selectedStatusFilter);
    }

    if (selectedLocationFilter) {
      list = list.filter((p) => {
        const studentLocation = (p.student?.location || '').toLowerCase();
        const courseLocation = (p.course?.location || '').toLowerCase();
        return studentLocation === selectedLocationFilter || courseLocation === selectedLocationFilter;
      });
    }

    if (minAmountFilter !== '') {
      const min = Number(minAmountFilter);
      list = list.filter((p) => Number((p.totalAmount ?? p.amount) || 0) >= min);
    }

    if (maxAmountFilter !== '') {
      const max = Number(maxAmountFilter);
      list = list.filter((p) => Number((p.totalAmount ?? p.amount) || 0) <= max);
    }

    return list;
  }, [
    data.payments,
    normalizedLocationFilter,
    selectedStudentFilter,
    selectedCourseFilter,
    selectedStatusFilter,
    selectedLocationFilter,
    minAmountFilter,
    maxAmountFilter,
    user.location,
    user.role
  ]);

  const totals = useMemo(() => {
    return filteredPayments.reduce((acc, p) => {
      const amount = Number(p.amount || 0);
      const totalAmount = Number((p.totalAmount ?? amount) || 0);
      const paidAmount = Number(p.paidAmount || 0);
      const remainingAmount = Math.max(0, totalAmount - paidAmount);
      acc.total += totalAmount;
      acc.paid += paidAmount;
      acc.pending += remainingAmount;
      return acc;
    }, { total: 0, paid: 0, pending: 0 });
  }, [filteredPayments]);

  const getRemainingAmount = (payment) => {
    const total = Number((payment.totalAmount ?? payment.amount) || 0);
    const paid = Number(payment.paidAmount || 0);
    return Math.max(0, total - paid);
  };

  const formatCurrency = (amount) => {
    const locale = i18n.language === 'nl' ? 'nl-NL' : 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(Number(amount || 0));
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!newPayment.studentId || !newPayment.totalAmount) return;

    const parsedTotal = Number(newPayment.totalAmount);
    if (Number.isNaN(parsedTotal) || parsedTotal <= 0) return;

    await addPayment({
      studentId: newPayment.studentId,
      courseId: newPayment.courseId || undefined,
      amount: parsedTotal,
      totalAmount: parsedTotal,
      description: newPayment.description || undefined,
      paymentType: newPayment.paymentType || 'school-year',
      schoolYear: newPayment.schoolYear || undefined,
      dueDate: newPayment.dueDate || undefined,
      paymentMethod: newPayment.paymentMethod || undefined,
      notes: newPayment.notes || undefined
    });

    setNewPayment({
      studentId: '',
      courseId: '',
      totalAmount: '',
      description: '',
      paymentType: 'school-year',
      schoolYear: '',
      dueDate: '',
      paymentMethod: 'bank transfer',
      notes: ''
    });
    setShowCreateForm(false);
  };

  const handleRecordPayment = async (payment) => {
    const remaining = getRemainingAmount(payment);
    if (remaining <= 0) return;

    const defaultAmount = String(remaining);
    const amountInput = window.prompt(`Aflossing registreren (openstaand: ${formatCurrency(remaining)})`, defaultAmount);
    if (!amountInput) return;

    const amount = Number(amountInput);
    if (Number.isNaN(amount) || amount <= 0) {
      window.alert('Voer een geldig aflossingsbedrag in.');
      return;
    }

    const methodInput = window.prompt('Betaalmethode (bank transfer, cash, pin, online, cheque)', payment.paymentMethod || 'bank transfer');
    if (!methodInput) return;

    await registerPayment(payment.id, amount, methodInput.trim(), `Aflossing geregistreerd: ${amount}`);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingPayment?.id) return;

    await updatePayment(editingPayment.id, {
      paymentMethod: editingPayment.paymentMethod,
      description: editingPayment.description,
      paymentType: editingPayment.paymentType,
      schoolYear: editingPayment.schoolYear,
      totalAmount: Number((editingPayment.totalAmount ?? editingPayment.amount) || 0),
      notes: editingPayment.notes,
      status: editingPayment.status
    });

    setEditingPayment(null);
  };

  const handleExportPDF = () => {
    const exportData = filteredPayments.map((p) => ({
      [t('payment.exportHeaders.student')]: p.student?.name || '-',
      [t('payment.exportHeaders.location')]: p.student?.location || '-',
      [t('payment.exportHeaders.course')]: p.course?.name || '-',
      [t('payment.exportHeaders.amount')]: p.amount,
      [t('payment.exportHeaders.status')]: p.status,
      [t('payment.exportHeaders.dueDate')]: p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.paidAt')]: p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.method')]: p.paymentMethod || '-'
    }));
    exportToPDF(exportData, 'NLBA_Admin_Payments', t('admin.payments'));
  };

  const handleExportExcel = () => {
    const exportData = filteredPayments.map((p) => ({
      [t('payment.exportHeaders.student')]: p.student?.name || '-',
      [t('payment.exportHeaders.location')]: p.student?.location || '-',
      [t('payment.exportHeaders.course')]: p.course?.name || '-',
      [t('payment.exportHeaders.amount')]: p.amount,
      [t('payment.exportHeaders.status')]: p.status,
      [t('payment.exportHeaders.dueDate')]: p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.paidAt')]: p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-',
      [t('payment.exportHeaders.method')]: p.paymentMethod || '-'
    }));
    exportToExcel(exportData, 'NLBA_Admin_Payments', t('payment.payments'), t('admin.payments'));
  };

  const resetFilters = () => {
    setSelectedStudentFilter('');
    setSelectedStatusFilter('');
    setSelectedCourseFilter('');
    setSelectedLocationFilter('');
    setMinAmountFilter('');
    setMaxAmountFilter('');
  };

  return (
    <div className="admin-payments-page">
      <div className="page-header">
        <h1>{t('admin.payments')}</h1>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleExportPDF} disabled={filteredPayments.length === 0}>
            {t('report.exportPDF')}
          </button>
          <button className="btn-secondary" onClick={handleExportExcel} disabled={filteredPayments.length === 0}>
            {t('report.exportExcel')}
          </button>
          <button className="btn-primary" onClick={() => setShowCreateForm((prev) => !prev)}>
            {t('payment.addPayment')}
          </button>
        </div>
      </div>

      <div className="filter-section">
        <div className="form-group">
          <label>{t('report.selectStudent')}</label>
          <select className="clean-select" value={selectedStudentFilter} onChange={(e) => setSelectedStudentFilter(e.target.value)}>
            <option value="">{t('common.allData')}...</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>{t('common.status')}</label>
          <select className="clean-select" value={selectedStatusFilter} onChange={(e) => setSelectedStatusFilter(e.target.value)}>
            <option value="">{t('common.allData')}...</option>
            <option value="pending">{t('payment.status.pending')}</option>
            <option value="partial">{t('payment.status.partial', 'Deels betaald')}</option>
            <option value="paid">{t('payment.status.paid')}</option>
            <option value="overdue">{t('payment.status.overdue')}</option>
            <option value="cancelled">{t('payment.status.cancelled')}</option>
          </select>
        </div>
        <div className="form-group compact-actions">
          <label>Extra filters</label>
          <div className="form-actions" style={{ marginTop: 0 }}>
            <button type="button" className="btn-secondary" onClick={() => setShowAdvancedFilters((prev) => !prev)}>
              {showAdvancedFilters ? 'Minder filters' : 'Meer filters'}
            </button>
            <button type="button" className="btn-secondary" onClick={resetFilters}>Reset</button>
          </div>
        </div>
      </div>

      {showAdvancedFilters && (
        <div className="filter-section advanced-filter-section">
          <div className="form-group">
            <label>{t('course.title')}</label>
            <select className="clean-select" value={selectedCourseFilter} onChange={(e) => setSelectedCourseFilter(e.target.value)}>
              <option value="">{t('common.allData')}...</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
              ))}
            </select>
          </div>
        <div className="form-group">
          <label>{t('location.title')}</label>
          <select className="clean-select" value={selectedLocationFilter} onChange={(e) => setSelectedLocationFilter(e.target.value)}>
            <option value="">{t('common.allData')}...</option>
            {(data.locations || []).map((l) => (
              <option key={l.id} value={l.id}>{translateLocation(t, l.id)}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>{t('payment.amount')} (min)</label>
          <input
            className="clean-input"
            type="number"
            min="0"
            step="0.01"
            value={minAmountFilter}
            onChange={(e) => setMinAmountFilter(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>{t('payment.amount')} (max)</label>
          <input
            className="clean-input"
            type="number"
            min="0"
            step="0.01"
            value={maxAmountFilter}
            onChange={(e) => setMaxAmountFilter(e.target.value)}
          />
        </div>
      </div>
      )}

      <Modal isOpen={showCreateForm} onClose={() => setShowCreateForm(false)} title={t('payment.addPayment')}>
          <form onSubmit={handleCreatePayment}>
            <div className="form-row">
              <div className="form-group">
                <label>{t('enrollment.student')}</label>
                <select
                  value={newPayment.studentId}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, studentId: e.target.value }))}
                  required
                >
                  <option value="">{t('common.select')}...</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('course.title')} ({t('common.optional')})</label>
                <select
                  value={newPayment.courseId}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, courseId: e.target.value }))}
                >
                  <option value="">-</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('payment.type')}</label>
                <select
                  value={newPayment.paymentType}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, paymentType: e.target.value }))}
                >
                  <option value="school-year">Schooljaar</option>
                  <option value="one-time">{t('payment.oneTime')}</option>
                  <option value="enrollment-fee">{t('payment.enrollmentFee')}</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('studyYear.title')}</label>
                <input
                  type="text"
                  value={newPayment.schoolYear}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, schoolYear: e.target.value }))}
                  placeholder="2025-2026"
                />
              </div>
              <div className="form-group">
                <label>{t('payment.title')}</label>
                <input
                  type="text"
                  value={newPayment.description}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Te betalen bedrag schooljaar</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newPayment.totalAmount}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, totalAmount: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('payment.dueDate')}</label>
                <input
                  type="date"
                  value={newPayment.dueDate}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, dueDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('payment.paymentMethod')}</label>
                <select
                  value={newPayment.paymentMethod}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  {paymentMethodOptions.map((method) => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('course.description')}</label>
                <input
                  type="text"
                  value={newPayment.notes}
                  onChange={(e) => setNewPayment((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowCreateForm(false)}>{t('common.cancel')}</button>
            </div>
          </form>
      </Modal>

      <div className="payments-summary">
        <div className="summary-card">
          <span className="summary-label">{t('payment.totalStudents')}</span>
          <span className="summary-value">{filteredPayments.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{t('payment.totalDue')}</span>
          <span className="summary-value">{formatCurrency(totals.total)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{t('payment.totalPaid')}</span>
          <span className="summary-value paid">{formatCurrency(totals.paid)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{t('payment.pending')}</span>
          <span className="summary-value pending">{formatCurrency(totals.pending)}</span>
        </div>
      </div>

      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>{t('enrollment.student')}</th>
              <th>{t('course.title')}</th>
              <th>Totaal</th>
              <th>Betaald</th>
              <th>Resterend</th>
              <th>{t('payment.paymentMethod')}</th>
              <th>{t('payment.dueDate')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.map((payment) => (
              <tr key={payment.id}>
                <td>
                  <div className="student-info">
                    <span className="student-name">{payment.student?.name || '-'}</span>
                    <span className="student-location">{translateLocation(t, (payment.student?.location || '').toLowerCase())}</span>
                  </div>
                </td>
                <td>{payment.course?.name || '-'}</td>
                <td>{formatCurrency(payment.totalAmount ?? payment.amount)}</td>
                <td>{formatCurrency(payment.paidAmount || 0)}</td>
                <td>{formatCurrency(getRemainingAmount(payment))}</td>
                <td>{payment.paymentMethod || '-'}</td>
                <td>{payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : '-'}</td>
                <td>
                  <span className={`status-badge status-${payment.status}`}>
                    {t(`payment.status.${payment.status}`, payment.status)}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-small"
                    onClick={() => setEditingPayment({ ...payment })}
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    className="btn-small"
                    onClick={() => handleRecordPayment(payment)}
                    disabled={getRemainingAmount(payment) <= 0}
                  >
                    Aflossing boeken
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={!!editingPayment} onClose={() => setEditingPayment(null)} title={t('common.edit')} size="sm">
        {editingPayment && (
          <form onSubmit={handleSaveEdit}>
            <div className="form-row">
              <div className="form-group">
                <label>{t('payment.paymentMethod')}</label>
                <select
                  value={editingPayment.paymentMethod || ''}
                  onChange={(e) => setEditingPayment((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  {paymentMethodOptions.map((method) => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('common.status')}</label>
                <select
                  value={editingPayment.status || 'pending'}
                  onChange={(e) => setEditingPayment((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="pending">{t('payment.status.pending')}</option>
                  <option value="partial">{t('payment.status.partial', 'Deels betaald')}</option>
                  <option value="paid">{t('payment.status.paid')}</option>
                  <option value="overdue">{t('payment.status.overdue')}</option>
                  <option value="cancelled">{t('payment.status.cancelled')}</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>{t('payment.title')}</label>
              <input
                type="text"
                value={editingPayment.description || ''}
                onChange={(e) => setEditingPayment((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('payment.type')}</label>
                <select
                  value={editingPayment.paymentType || 'one-time'}
                  onChange={(e) => setEditingPayment((prev) => ({ ...prev, paymentType: e.target.value }))}
                >
                  <option value="school-year">Schooljaar</option>
                  <option value="one-time">{t('payment.oneTime')}</option>
                  <option value="enrollment-fee">{t('payment.enrollmentFee')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>Totaalbedrag</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editingPayment.totalAmount ?? editingPayment.amount ?? ''}
                  onChange={(e) => setEditingPayment((prev) => ({ ...prev, totalAmount: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('studyYear.title')}</label>
              <input
                type="text"
                value={editingPayment.schoolYear || ''}
                onChange={(e) => setEditingPayment((prev) => ({ ...prev, schoolYear: e.target.value }))}
                placeholder="2025-2026"
              />
            </div>
            <div className="form-group">
              <label>{t('course.description')}</label>
              <input
                type="text"
                value={editingPayment.notes || ''}
                onChange={(e) => setEditingPayment((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setEditingPayment(null)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default AdminPayments;
